import { Head, router } from "@inertiajs/react"
import { Button, Card, Flex, Heading, Table } from "@radix-ui/themes"
import { Copy, Plus, Send, Trash2 } from "lucide-react"
import { z } from "zod"

import LinkButton from "@/components/link-button"
import ProjectLayout from "@/layouts/project-layout"
import { formatDateTime } from "@/lib/datetime"
import { newProjectEmailPath, projectDashboardPath, projectEmailPath } from "@/routes"
import { EmailSchema, ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  emails: z.array(EmailSchema),
})

type Props = z.infer<typeof PropsSchema>

export default function EmailsIndex(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { emails, project } = validatedProps

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Email Templates" },
      ]}
    >
      <Head title="Email Templates" />

      <Flex direction="column" gap="6" p="6">
        <Flex justify="between" align="center">
          <Heading size="6" weight="medium">
            Email Templates
          </Heading>

          <LinkButton href={newProjectEmailPath(project.slug)} variant="classic">
            <Plus className="mr-1 h-4 w-4" />
            New email template
          </LinkButton>
        </Flex>

        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Custom ID</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Subject</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Style</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {emails.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5} style={{ textAlign: "center", color: "var(--gray-11)" }}>
                    No email templates found
                  </Table.Cell>
                </Table.Row>
              ) : (
                emails.map((email) => (
                  <Table.Row key={email.id}>
                    <Table.Cell
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => router.visit(projectEmailPath(project.slug, email.id))}
                    >
                      <code>{email.custom_id}</code>
                    </Table.Cell>
                    <Table.RowHeaderCell
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => router.visit(projectEmailPath(project.slug, email.id))}
                    >
                      {email.subject ?? "Untitled"}
                    </Table.RowHeaderCell>
                    <Table.Cell
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => router.visit(projectEmailPath(project.slug, email.id))}
                    >
                      {email.style}
                    </Table.Cell>
                    <Table.Cell
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => router.visit(projectEmailPath(project.slug, email.id))}
                    >
                      {formatDateTime(email.created_at)}
                    </Table.Cell>
                    <Table.Cell>
                      <Flex gap="2">
                        <Button
                          size="1"
                          variant="soft"
                          onClick={(e) => {
                            e.stopPropagation()
                            router.post(`/projects/${project.slug}/emails/${email.id}/duplicate`)
                          }}
                        >
                          <Copy className="mr-1 h-3 w-3" />
                          Duplicate
                        </Button>
                        <Button
                          size="1"
                          variant="soft"
                          onClick={(e) => {
                            e.stopPropagation()
                            router.post(`/projects/${project.slug}/emails/${email.id}/send_test`)
                          }}
                        >
                          <Send className="mr-1 h-3 w-3" />
                          Send test
                        </Button>
                        <Button
                          size="1"
                          variant="soft"
                          color="red"
                          onClick={(e) => {
                            e.stopPropagation()
                            if (
                              confirm(
                                `Are you sure you want to delete the email template "${email.subject ?? "Untitled"}"?`,
                              )
                            ) {
                              router.delete(`/projects/${project.slug}/emails/${email.id}`)
                            }
                          }}
                        >
                          <Trash2 className="mr-1 h-3 w-3" />
                          Delete
                        </Button>
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card>
      </Flex>
    </ProjectLayout>
  )
}
